import { getMapStopLayerIds } from "@catchy/region-config";
import { buildStopIconExpression } from "./useStopIcons";
import { normalizeHexColor } from "./color";
import {
  activeStopIconSizeExpression,
  scooterIconSizeExpression,
  selectedScooterIconSizeExpression,
  stopIconSizeExpression,
} from "./mapIconSizing";
import { getRouteLineContrastColor } from "./tripPageViewModel";

export const vehicleLayerId = "vehicle-layer";
export const pointerLayerId = "pointer-layer";
export const selectedVehicleSourceId = "selected-vehicle";
export const selectedVehicleCircleLayerId = "selected-vehicle-circle";
export const selectedVehicleLayerId = "selected-vehicle-layer";
export const selectedTripShapeSourceId = "selected-trip-shape";
export const selectedTripShapeCasingLayerId = "selected-trip-shape-casing";
export const selectedTripShapeLayerId = "selected-trip-shape-layer";
export const scooterHitLayerId = "scooter-hit-layer";
export const scooterLayerId = "scooter-layer";
export const selectedScooterSourceId = "selected-scooter";
export const selectedScooterLayerId = "selected-scooter-layer";
export const scooterSourceId = "scooters";

const micromobilityMinZoom = 13;

const routeLayerAnchorIds = [
  "road-label",
  "road-label-local",
  "atlas-hybrid-road-label",
  "atlas-hybrid-local-road-label",
  "v2-road-label",
  "road-label-simple",
];

function getRouteLayerAnchorId(mapInstance) {
  return routeLayerAnchorIds.find((layerId) => mapInstance.getLayer(layerId));
}

function moveLayerBefore(mapInstance, layerId, beforeLayerId) {
  if (
    layerId === beforeLayerId ||
    !mapInstance.getLayer(layerId) ||
    !mapInstance.getLayer(beforeLayerId)
  ) {
    return false;
  }

  mapInstance.moveLayer(layerId, beforeLayerId);
  return true;
}

function orderRegularScooterLayers(mapInstance) {
  for (const layerId of [scooterHitLayerId, scooterLayerId]) {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.moveLayer(layerId);
    }
  }
}

function orderSelectedScooterLayer(mapInstance) {
  if (!mapInstance.getLayer(selectedScooterLayerId)) {
    return;
  }

  mapInstance.moveLayer(selectedScooterLayerId);
}

function orderScooterLayers(mapInstance) {
  orderRegularScooterLayers(mapInstance);
  orderSelectedScooterLayer(mapInstance);
}

function bringSelectedVehicleToFront(mapInstance) {
  for (const layerId of [
    selectedVehicleCircleLayerId,
    selectedVehicleLayerId,
  ]) {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.moveLayer(layerId);
    }
  }
}

function bringSelectedTripShapeToFront(mapInstance) {
  const { stops: stopLayerId, activeStops: activeStopLayerId } =
    getMapStopLayerIds();
  const routeLayerAnchorId = getRouteLayerAnchorId(mapInstance);
  const beforeLayerId = routeLayerAnchorId
    ? routeLayerAnchorId
    : mapInstance.getLayer(stopLayerId)
      ? stopLayerId
      : mapInstance.getLayer(activeStopLayerId)
        ? activeStopLayerId
        : null;

  for (const layerId of [
    selectedTripShapeCasingLayerId,
    selectedTripShapeLayerId,
  ]) {
    if (mapInstance.getLayer(layerId)) {
      if (beforeLayerId) {
        mapInstance.moveLayer(layerId, beforeLayerId);
      } else {
        mapInstance.moveLayer(layerId);
      }
    }
  }
}

function bringSelectedStopsToFront(mapInstance) {
  const { activeStops } = getMapStopLayerIds();
  if (mapInstance.getLayer(activeStops)) {
    mapInstance.moveLayer(activeStops);
  }
}

function bringAllSelectedOverlaysToFront(mapInstance) {
  bringSelectedStopsToFront(mapInstance);
  orderSelectedScooterLayer(mapInstance);
  bringSelectedVehicleToFront(mapInstance);
}

const noActiveStopFilter = [
  "==",
  ["to-string", ["get", "stop_id"]],
  "__none__",
];

const buildActiveStopIconExpression = (isDark, preferSatellite) => {
  const suffix = isDark || preferSatellite ? "dark" : "light";
  return [
    "case",
    // TODO: Remove the cableCar fallback after deployed stop data has fully moved to cable_car.
    ["in", ["get", "stop_type"], ["literal", ["cable_car", "cableCar"]]],
    `active-stop-cable-car-${suffix}`,
    ["==", ["get", "stop_type"], "ferry"],
    `active-stop-ferry-${suffix}`,
    ["==", ["get", "stop_type"], "train"],
    `active-stop-train-${suffix}`,
    `active-stop-bus-${suffix}`,
  ];
};

export const stopHoverOpacityExpression = [
  "case",
  ["boolean", ["feature-state", "hover"], false],
  0,
  1,
];

export const vehicleRouteBadgeIconSizeExpression = [
  "interpolate",
  ["linear"],
  ["zoom"],
  10,
  ["*", 0.146, ["coalesce", ["get", "routeBadgeScale"], 1]],
  16,
  ["*", 0.302, ["coalesce", ["get", "routeBadgeScale"], 1]],
  22,
  ["*", 0.672, ["coalesce", ["get", "routeBadgeScale"], 1]],
];

const vehiclePointerIconSizeExpression = [
  "interpolate",
  ["linear"],
  ["zoom"],
  10,
  0.584,
  16,
  1.208,
  22,
  2.688,
];

const selectedVehicleIconSizeExpression = [
  "interpolate",
  ["linear"],
  ["zoom"],
  10,
  0.584,
  16,
  1.208,
  22,
  2.688,
];

export function filterOutZeroVehicles(geojson) {
  if (!geojson || !geojson.features) return geojson;

  const features = geojson.features.filter((feature) => {
    const coordinates = feature?.geometry?.coordinates;
    return (
      Array.isArray(coordinates) &&
      coordinates.length >= 2 &&
      !(coordinates[0] === 0 && coordinates[1] === 0)
    );
  });

  return { ...geojson, features };
}

function addOptionalSourceLayer(layer, sourceLayer) {
  if (!sourceLayer) {
    return layer;
  }

  return {
    ...layer,
    "source-layer": sourceLayer,
  };
}

function buildRuntimeStopSourceDefinition(stopSourceConfig) {
  if (stopSourceConfig.type !== "vector") {
    return {
      type: stopSourceConfig.type || "geojson",
      data: stopSourceConfig.data || "/stops.geojson",
    };
  }

  return {
    type: "vector",
    url: stopSourceConfig.url,
    promoteId: stopSourceConfig.promoteId,
  };
}

function addRuntimeStopLayers({
  mapInstance,
  isDark,
  preferSatellite,
  favourites,
  stopLayerIds,
  stopSourceConfig,
}) {
  if (!mapInstance.getLayer(stopLayerIds.stops)) {
    mapInstance.addLayer(
      addOptionalSourceLayer(
        {
          id: stopLayerIds.stops,
          type: "symbol",
          source: stopSourceConfig.sourceId,
          layout: {
            "text-field": " ",
            "text-font": ["Bus Regular"],
            "icon-size": stopIconSizeExpression,
            "icon-image": buildStopIconExpression(
              isDark,
              preferSatellite,
              favourites,
            ),
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            "icon-anchor": "bottom",
            visibility: "visible",
          },
          paint: {
            "icon-opacity": stopHoverOpacityExpression,
          },
        },
        stopSourceConfig.sourceLayer,
      ),
    );
  } else {
    mapInstance.setLayoutProperty(
      stopLayerIds.stops,
      "icon-image",
      buildStopIconExpression(isDark, preferSatellite, favourites),
    );
    mapInstance.setLayoutProperty(stopLayerIds.stops, "visibility", "visible");
    mapInstance.setPaintProperty(
      stopLayerIds.stops,
      "icon-opacity",
      stopHoverOpacityExpression,
    );
  }

  if (!mapInstance.getLayer(stopLayerIds.activeStops)) {
    mapInstance.addLayer(
      addOptionalSourceLayer(
        {
          id: stopLayerIds.activeStops,
          type: "symbol",
          source: stopSourceConfig.sourceId,
          filter: noActiveStopFilter,
          layout: {
            "icon-image": buildActiveStopIconExpression(isDark, preferSatellite),
            "icon-size": activeStopIconSizeExpression,
            "text-field": " ",
            "text-font": ["Bus Regular"],
            "icon-anchor": "bottom",
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            visibility: "none",
          },
        },
        stopSourceConfig.sourceLayer,
      ),
    );
  } else {
    mapInstance.setLayoutProperty(
      stopLayerIds.activeStops,
      "icon-image",
      buildActiveStopIconExpression(isDark, preferSatellite),
    );
  }
}

export async function ensureRuntimeStopLayers({
  mapInstance,
  isDark,
  preferSatellite,
  favourites,
  stopLayerIds,
  stopSourceConfig,
}) {
  if (!mapInstance || !stopSourceConfig?.runtime) return;

  if (mapInstance.getSource(stopSourceConfig.sourceId)) {
    addRuntimeStopLayers({
      mapInstance,
      isDark,
      preferSatellite,
      favourites,
      stopLayerIds,
      stopSourceConfig,
    });
    bringAllSelectedOverlaysToFront(mapInstance);
    return;
  }

  mapInstance.addSource(
    stopSourceConfig.sourceId,
    buildRuntimeStopSourceDefinition(stopSourceConfig),
  );

  addRuntimeStopLayers({
    mapInstance,
    isDark,
    preferSatellite,
    favourites,
    stopLayerIds,
    stopSourceConfig,
  });

  bringAllSelectedOverlaysToFront(mapInstance);
}

export function addVehicleSourceAndLayer(mapInstance, vehiclePositions) {
  if (!mapInstance.getSource("vehicles")) {
    mapInstance.addSource("vehicles", {
      type: "geojson",
      data: filterOutZeroVehicles(vehiclePositions),
    });

    mapInstance.addLayer({
      id: vehicleLayerId,
      type: "symbol",
      source: "vehicles",
      layout: {
        "icon-image": [
          "coalesce",
          ["get", "routeImageId"],
          ["concat", "route-", ["get", "routeId"]],
        ],
        "icon-size": vehicleRouteBadgeIconSizeExpression,
        "icon-allow-overlap": true,
      },
    });

    mapInstance.addLayer({
      id: pointerLayerId,
      type: "symbol",
      source: "vehicles",
      layout: {
        "icon-image": "vehicle-arrow",
        "icon-rotate": [
          "case",
          ["==", ["get", "routeType"], 2],
          ["+", ["get", "bearing"], 22],
          ["==", ["get", "routeType"], 4],
          ["+", ["get", "bearing"], -15],
          ["get", "bearing"],
        ],
        "icon-size": vehiclePointerIconSizeExpression,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-anchor": "center",
        "icon-offset": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          ["literal", [0, -1]],
          16,
          ["literal", [0, -1]],
          22,
          ["literal", [0, -6]],
        ],
        "icon-rotation-alignment": "map",
      },
    });
  }
}

export function updateSelectedVehicle(
  mapInstance,
  selectedItem,
  vehiclePositions,
) {
  if (!mapInstance) return;
  const selectedVehicleSource = mapInstance.getSource(selectedVehicleSourceId);

  if (selectedItem?.type === "vehicle") {
    const selectedVehicleData = vehiclePositions.features.find((feature) => {
      const match = feature?.properties.vehicleId === selectedItem.id;
      if (!match) return false;
      const coordinates = feature?.geometry?.coordinates;
      return (
        Array.isArray(coordinates) &&
        coordinates.length >= 2 &&
        !(coordinates[0] === 0 && coordinates[1] === 0)
      );
    });

    if (selectedVehicleSource) {
      selectedVehicleSource.setData({
        type: "FeatureCollection",
        features: selectedVehicleData ? [selectedVehicleData] : [],
      });
    } else {
      mapInstance.addSource(selectedVehicleSourceId, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      mapInstance.addLayer({
        id: selectedVehicleCircleLayerId,
        type: "circle",
        source: selectedVehicleSourceId,
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            20,
            16,
            38,
            22,
            70,
          ],
          "circle-color": "#000000",
          "circle-blur": 1.5,
          "circle-opacity": 0.5,
          "circle-translate": [0, 3],
        },
      });
      mapInstance.addLayer({
        id: selectedVehicleLayerId,
        type: "symbol",
        source: selectedVehicleSourceId,
        layout: {
          "icon-image": "vehicle-position-marker",
          "icon-rotate": [
            "case",
            ["==", ["get", "routeType"], 2],
            ["+", ["get", "bearing"], 22],
            ["==", ["get", "routeType"], 4],
            ["+", ["get", "bearing"], -15],
            ["get", "bearing"],
          ],
          "icon-size": selectedVehicleIconSizeExpression,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-anchor": "center",
          "icon-offset": ["literal", [0, 0]],
          "icon-rotation-alignment": "map",
        },
        paint: { "icon-opacity": 0.9 },
      });

      if (selectedVehicleData) {
        mapInstance.getSource(selectedVehicleSourceId).setData({
          type: "FeatureCollection",
          features: [selectedVehicleData],
        });
      }
    }

    bringAllSelectedOverlaysToFront(mapInstance);
  } else if (selectedVehicleSource) {
    mapInstance.removeLayer(selectedVehicleLayerId);
    mapInstance.removeLayer(selectedVehicleCircleLayerId);
    mapInstance.removeSource(selectedVehicleSourceId);
  }
}

export function updateVehicleSource(
  mapInstance,
  selectedItem,
  vehiclePositions,
) {
  if (!mapInstance) return;

  const validVehiclePositions = filterOutZeroVehicles(vehiclePositions);
  let filteredVehiclePositions = validVehiclePositions;

  if (selectedItem?.type === "vehicle") {
    filteredVehiclePositions = {
      ...validVehiclePositions,
      features: validVehiclePositions.features.filter(
        (feature) => feature.properties.vehicleId !== selectedItem.id,
      ),
    };
  }

  if (mapInstance.getSource("vehicles")) {
    mapInstance.getSource("vehicles").setData(filteredVehiclePositions);
  } else {
    addVehicleSourceAndLayer(mapInstance, filteredVehiclePositions);
  }

  bringAllSelectedOverlaysToFront(mapInstance);
}

/**
 * Selection transitions must keep the regular and selected vehicle sources in
 * lockstep. This only touches an already-installed regular source, so initial
 * layer creation can still wait for its route-badge images.
 */
export function syncExistingVehicleLayers(
  mapInstance,
  selectedItem,
  vehiclePositions,
) {
  if (!mapInstance?.getSource("vehicles")) {
    return false;
  }

  updateVehicleSource(mapInstance, selectedItem, vehiclePositions);
  updateSelectedVehicle(mapInstance, selectedItem, vehiclePositions);
  return true;
}

export function removeVehicleLayers(mapInstance) {
  if (!mapInstance) {
    return;
  }

  for (const layerId of [
    selectedVehicleLayerId,
    selectedVehicleCircleLayerId,
    pointerLayerId,
    vehicleLayerId,
  ]) {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
  }

  for (const sourceId of [selectedVehicleSourceId, "vehicles"]) {
    if (mapInstance.getSource(sourceId)) {
      mapInstance.removeSource(sourceId);
    }
  }
}

const scooterIconImageExpression = [
  "coalesce",
  ["get", "markerImageId"],
  [
    "case",
    ["==", ["get", "formFactor"], "bicycle"],
    "micromobility-flamingo-bike",
    "micromobility-flamingo-scooter",
  ],
];

const selectedScooterIconImageExpression = [
  "coalesce",
  ["get", "activeMarkerImageId"],
  [
    "case",
    ["==", ["get", "markerImageId"], "micromobility-jolt-scooter"],
    "micromobility-jolt-scooter-active",
    ["==", ["get", "formFactor"], "bicycle"],
    "micromobility-flamingo-bike-active",
    "micromobility-flamingo-scooter-active",
  ],
];

export function addScooterSourceAndLayer(mapInstance, scooterPositions) {
  if (mapInstance.getSource(scooterSourceId)) {
    return;
  }

  mapInstance.addSource(scooterSourceId, {
    type: "geojson",
    data: filterOutZeroVehicles(scooterPositions),
  });

  mapInstance.addLayer({
    id: scooterHitLayerId,
    type: "circle",
    source: scooterSourceId,
    minzoom: micromobilityMinZoom,
    paint: {
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        10,
        16,
        14,
        22,
        18,
        30,
        22,
        40,
      ],
      "circle-color": "#000000",
      "circle-opacity": 0,
    },
  });

  mapInstance.addLayer({
    id: scooterLayerId,
    type: "symbol",
    source: scooterSourceId,
    minzoom: micromobilityMinZoom,
    layout: {
      "icon-image": scooterIconImageExpression,
      "icon-size": scooterIconSizeExpression,
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  orderScooterLayers(mapInstance);
}

function updateSelectedScooter(mapInstance, selectedItem, scooterPositions) {
  const selectedScooterSource = mapInstance.getSource(selectedScooterSourceId);

  if (selectedItem?.type === "scooter") {
    const selectedScooterData = scooterPositions.features.find((feature) => {
      const match = feature?.properties.vehicleId === selectedItem.id;
      if (!match) return false;
      const coordinates = feature?.geometry?.coordinates;
      return (
        Array.isArray(coordinates) &&
        coordinates.length >= 2 &&
        !(coordinates[0] === 0 && coordinates[1] === 0)
      );
    });

    if (selectedScooterSource) {
      selectedScooterSource.setData({
        type: "FeatureCollection",
        features: selectedScooterData ? [selectedScooterData] : [],
      });
    } else {
      mapInstance.addSource(selectedScooterSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: selectedScooterData ? [selectedScooterData] : [],
        },
      });

      mapInstance.addLayer({
        id: selectedScooterLayerId,
        type: "symbol",
        source: selectedScooterSourceId,
        layout: {
          "icon-image": selectedScooterIconImageExpression,
          "icon-size": selectedScooterIconSizeExpression,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-anchor": "bottom",
        },
      });
    }
  } else if (selectedScooterSource) {
    if (mapInstance.getLayer(selectedScooterLayerId)) {
      mapInstance.removeLayer(selectedScooterLayerId);
    }
    mapInstance.removeSource(selectedScooterSourceId);
  }
}

export function updateScooterSource(
  mapInstance,
  selectedItem,
  scooterPositions,
) {
  // Reconciliation is owned by the style lifecycle, which only invokes this
  // for its current style generation. A transient MapLibre `isStyleLoaded()`
  // false here can otherwise drop the final off → on update with no retry.
  if (!mapInstance) return;

  const validScooterPositions = filterOutZeroVehicles(scooterPositions);
  const filteredScooterPositions =
    selectedItem?.type === "scooter"
      ? {
          ...validScooterPositions,
          features: validScooterPositions.features.filter(
            (feature) => feature.properties.vehicleId !== selectedItem.id,
          ),
        }
      : validScooterPositions;

  if (mapInstance.getSource(scooterSourceId)) {
    mapInstance.getSource(scooterSourceId).setData(filteredScooterPositions);
  } else {
    addScooterSourceAndLayer(mapInstance, filteredScooterPositions);
  }

  updateSelectedScooter(mapInstance, selectedItem, validScooterPositions);
  orderScooterLayers(mapInstance);
  bringAllSelectedOverlaysToFront(mapInstance);
}

export function removeScooterLayers(mapInstance) {
  if (!mapInstance) {
    return;
  }

  for (const layerId of [
    selectedScooterLayerId,
    scooterLayerId,
    scooterHitLayerId,
  ]) {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
  }

  for (const sourceId of [selectedScooterSourceId, scooterSourceId]) {
    if (mapInstance.getSource(sourceId)) {
      mapInstance.removeSource(sourceId);
    }
  }
}

const tripMapRouteLineWidthExpression = [
  "interpolate",
  ["exponential", 1.5],
  ["zoom"],
  13,
  5,
  14,
  10,
  18,
  23,
];

const tripMapRouteInnerLineWidthExpression = [
  "interpolate",
  ["exponential", 1.5],
  ["zoom"],
  13,
  2.5,
  14,
  6,
  18,
  15,
];

export function ensureTripMapRouteLayers(
  mapInstance,
  { shape, outerRouteColor, innerRouteColor },
) {
  if (!mapInstance) {
    return;
  }

  if (!mapInstance.getSource("routelayer")) {
    mapInstance.addSource("routelayer", {
      type: "geojson",
      lineMetrics: true,
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates:
            shape?.map((point) => [point.shape_pt_lon, point.shape_pt_lat]) ??
            [],
        },
      },
    });
  }

  const routeShapeBeforeLayerId = getRouteLayerAnchorId(mapInstance);

  if (!mapInstance.getLayer("routelayer")) {
    mapInstance.addLayer(
      {
        id: "routelayer",
        type: "line",
        source: "routelayer",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": outerRouteColor,
          "line-width": tripMapRouteLineWidthExpression,
        },
      },
      routeShapeBeforeLayerId,
    );

    mapInstance.addLayer(
      {
        id: "routelayerinner",
        type: "line",
        source: "routelayer",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": innerRouteColor,
          "line-width": tripMapRouteInnerLineWidthExpression,
        },
      },
      routeShapeBeforeLayerId,
    );
  } else {
    mapInstance.moveLayer("routelayer", routeShapeBeforeLayerId);
    mapInstance.moveLayer("routelayerinner", routeShapeBeforeLayerId);
  }

  return {
    routeShapeBeforeLayerId,
  };
}

const selectedTripShapeFadeDurationMs = 180;
const selectedTripShapeRemovalTimers = new WeakMap();
const selectedTripShapeLayerIds = [
  selectedTripShapeLayerId,
  selectedTripShapeCasingLayerId,
];

function clearSelectedTripShapeRemovalTimer(mapInstance) {
  const removalTimer = selectedTripShapeRemovalTimers.get(mapInstance);

  if (removalTimer) {
    globalThis.clearTimeout(removalTimer);
    selectedTripShapeRemovalTimers.delete(mapInstance);
  }
}

function removeSelectedTripShape(mapInstance) {
  for (const layerId of selectedTripShapeLayerIds) {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
  }

  if (mapInstance.getSource(selectedTripShapeSourceId)) {
    mapInstance.removeSource(selectedTripShapeSourceId);
  }
}

function setSelectedTripShapeOpacity(mapInstance, opacity) {
  for (const layerId of selectedTripShapeLayerIds) {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.setPaintProperty(layerId, "line-opacity", opacity);
    }
  }
}

function fadeOutSelectedTripShape(mapInstance) {
  clearSelectedTripShapeRemovalTimer(mapInstance);

  if (!mapInstance.getSource(selectedTripShapeSourceId)) {
    return;
  }

  setSelectedTripShapeOpacity(mapInstance, 0);

  const removalTimer = globalThis.setTimeout(() => {
    try {
      removeSelectedTripShape(mapInstance);
    } finally {
      selectedTripShapeRemovalTimers.delete(mapInstance);
    }
  }, selectedTripShapeFadeDurationMs + 40);

  selectedTripShapeRemovalTimers.set(mapInstance, removalTimer);
}

function fadeInSelectedTripShape(mapInstance) {
  const schedule =
    typeof globalThis.requestAnimationFrame === "function"
      ? globalThis.requestAnimationFrame
      : (callback) => globalThis.setTimeout(callback, 0);

  schedule(() => {
    setSelectedTripShapeOpacity(mapInstance, 1);
  });
}

export function updateSelectedTripShape(mapInstance, selectedTripShape) {
  if (!mapInstance) {
    return;
  }

  if (!selectedTripShape?.shape?.length) {
    fadeOutSelectedTripShape(mapInstance);
    return;
  }

  clearSelectedTripShapeRemovalTimer(mapInstance);

  const coordinates = selectedTripShape.shape
    .map((point) => [Number(point.shape_pt_lon), Number(point.shape_pt_lat)])
    .filter(
      ([longitude, latitude]) =>
        Number.isFinite(longitude) && Number.isFinite(latitude),
    );

  if (coordinates.length < 2) {
    updateSelectedTripShape(mapInstance, null);
    return;
  }

  const routeColor = normalizeHexColor(selectedTripShape.routeColor, "#222B2D");
  const routeLineContrastColor = getRouteLineContrastColor(
    routeColor,
    Boolean(selectedTripShape.isDark),
  );

  const data = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates,
    },
    properties: {
      outerRouteColor: selectedTripShape.isDark
        ? routeLineContrastColor
        : routeColor,
      innerRouteColor: selectedTripShape.isDark
        ? routeColor
        : routeLineContrastColor,
    },
  };

  const source = mapInstance.getSource(selectedTripShapeSourceId);

  if (source) {
    source.setData(data);
    setSelectedTripShapeOpacity(mapInstance, 1);
    bringSelectedTripShapeToFront(mapInstance);
    bringAllSelectedOverlaysToFront(mapInstance);
    return;
  }

  mapInstance.addSource(selectedTripShapeSourceId, {
    type: "geojson",
    data,
  });

  const routeShapeBeforeLayerId = getRouteLayerAnchorId(mapInstance);

  mapInstance.addLayer(
    {
      id: selectedTripShapeCasingLayerId,
      type: "line",
      source: selectedTripShapeSourceId,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": ["get", "outerRouteColor"],
        "line-width": tripMapRouteLineWidthExpression,
        "line-opacity": 0,
        "line-opacity-transition": {
          duration: selectedTripShapeFadeDurationMs,
        },
      },
    },
    routeShapeBeforeLayerId,
  );

  mapInstance.addLayer(
    {
      id: selectedTripShapeLayerId,
      type: "line",
      source: selectedTripShapeSourceId,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": ["get", "innerRouteColor"],
        "line-width": tripMapRouteInnerLineWidthExpression,
        "line-opacity": 0,
        "line-opacity-transition": {
          duration: selectedTripShapeFadeDurationMs,
        },
      },
    },
    routeShapeBeforeLayerId,
  );

  fadeInSelectedTripShape(mapInstance);
  bringSelectedTripShapeToFront(mapInstance);
  bringAllSelectedOverlaysToFront(mapInstance);
}
